// Public corporate activation endpoints (core-lambda).
// Reuses axiosAssessmentInstance — REACT_APP_BASE_URL_ASSESSMENT is the
// core-lambda host even though the env var name suggests otherwise.
//
// These endpoints accept anonymous traffic. The interceptor only attaches
// Authorization when an idtoken cookie exists, so unauthenticated callers
// work without modification.

import { axiosAssessmentInstance } from "../AxiosConfiguration";

const unwrap = (response, fallback = null) =>
  response?.data?.payload ?? fallback;

/**
 * GET /api/enterprises/by-domain/{slug}
 * 404 → throws; caller is expected to render the inactive-corporate fallback.
 * @param {string} slug
 * @returns {Promise<import('../types/corporate').CorporateBranding>}
 */
export const getCorporateBranding = async (slug) => {
  const response = await axiosAssessmentInstance.get(
    `/api/enterprises/by-domain/${encodeURIComponent(slug)}`
  );
  return unwrap(response);
};

/**
 * GET /api/enterprises/by-domain/{slug}/content/{page_key}
 * Returns published content. Sections may be missing — UI must handle absence.
 * @param {string} slug
 * @param {string} pageKey - e.g. "activation"
 * @returns {Promise<import('../types/corporate').CorporatePageContent>}
 */
export const getCorporateContent = async (slug, pageKey) => {
  const response = await axiosAssessmentInstance.get(
    `/api/enterprises/by-domain/${encodeURIComponent(slug)}/content/${encodeURIComponent(pageKey)}`
  );
  return unwrap(response, { sections: {} });
};

/**
 * GET /api/enterprises/by-domain/{slug}/lookup-values/{kind}
 * @param {string} slug
 * @param {'departments'|'roles'} kind
 * @returns {Promise<{kind: string, items: import('../types/corporate').LookupValueLite[]}>}
 */
export const getCorporateLookupValues = async (slug, kind) => {
  const response = await axiosAssessmentInstance.get(
    `/api/enterprises/by-domain/${encodeURIComponent(slug)}/lookup-values/${encodeURIComponent(kind)}`
  );
  return unwrap(response, { kind, items: [] });
};

/**
 * POST /api/enterprise/activate/check-eligibility
 * Always returns 200 — eligibility is conveyed by `payload.eligible`.
 * @param {import('../types/corporate').CheckEligibilityRequest} body
 * @returns {Promise<import('../types/corporate').CheckEligibilityResponse>}
 */
export const checkEligibility = async (body) => {
  const response = await axiosAssessmentInstance.post(
    "/api/enterprise/activate/check-eligibility",
    body
  );
  return unwrap(response);
};
